import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AttachmentsParams,
  AttachmentsResponse,
  ClientCommunicationsParams,
  ClientCommunicationsResponse,
} from '../types/client-communications-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const clientCommunicationsApiClient = createApi({
  reducerPath: 'clientCommunicationsApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'ClientCommunications',
    'ClientAttachments',
    'ClientCommsUnread',
  ] as const,
  endpoints: (builder) => ({
    getClientCommunications: builder.query<
      ClientCommunicationsResponse,
      { matterId: string; params?: ClientCommunicationsParams }
    >({
      query: ({ matterId, params }) => ({
        url: `api/matters/${matterId}/client-communications/`,
        params,
      }),
      providesTags: (result, error, { matterId }) => [
        { type: 'ClientCommunications', id: matterId },
      ],
    }),

    getClientAttachments: builder.query<
      AttachmentsResponse,
      { matterId: string; params?: AttachmentsParams }
    >({
      query: ({ matterId, params }) => ({
        url: `api/matters/${matterId}/client-communications/attachments/`,
        params,
      }),
      providesTags: (result, error, { matterId }) => [
        { type: 'ClientAttachments', id: matterId },
      ],
    }),

    getUnreadClientCommsCount: builder.query<
      { has_unreads: boolean; unread_sms_count: number; unread_email_count: number },
      { matterId: string }
    >({
      query: ({ matterId }) => `api/matters/${matterId}/client-communications/unread-count/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'ClientCommsUnread', id: matterId },
      ],
    }),

    markCommunicationsViewed: builder.mutation<void, { matterId: string }>({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/client-communications/mark-viewed/`,
        method: 'POST',
      }),
      invalidatesTags: ['ClientCommsUnread'],
    }),

    syncEmails: builder.mutation<
      { success: boolean; synced_count: number; errors?: Array<{ conversation_id: string; error: string }> },
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/client-communications/sync-emails/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'ClientCommunications', id: matterId },
      ],
    }),
  }),
});

export const {
  useGetClientCommunicationsQuery,
  useLazyGetClientCommunicationsQuery,
  useGetClientAttachmentsQuery,
  useLazyGetClientAttachmentsQuery,
  useGetUnreadClientCommsCountQuery,
  useMarkCommunicationsViewedMutation,
  useSyncEmailsMutation,
} = clientCommunicationsApiClient;
